import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type AdminInstitutionItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, StatusBadge, Tabs, Dropdown, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

const STATUSES = ['all', 'active', 'suspended', 'archived'];

export default function AdminInstitutionsPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [status, setStatus] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const q = useApi(() => admin.institutions(status === 'all' ? undefined : status, 200), [status, refreshKey]);
  const rows = q.status === 'success' ? q.data : [];

  async function setStat(r: AdminInstitutionItem, next: string) {
    try {
      await admin.setInstitutionStatus(r.id, next);
      toast.success(t('inst.changed'));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    }
  }

  const columns: Column<AdminInstitutionItem>[] = [
    { key: 'legal_name', header: t('col.name'), render: (r) => r.display_name || r.legal_name },
    { key: 'type_id', header: t('col.type'), width: '160px', mono: true, render: (r) => r.type_id },
    { key: 'license_type', header: t('inst.license'), width: '90px', render: (r) => r.license_type },
    { key: 'seats', header: t('inst.seats'), width: '90px', align: 'right', mono: true,
      render: (r) => `${r.license_seats_used}/${r.license_seats}` },
    { key: 'status', header: t('col.status'), width: '110px', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'actions', header: t('col.actions'), width: '70px', align: 'right',
      render: (r) => (
        <Dropdown
          trigger="⋯"
          items={[
            { label: t('inst.activate'), onClick: () => setStat(r, 'active'), disabled: r.status === 'active' },
            { label: t('inst.suspend'), onClick: () => setStat(r, 'suspended'), disabled: r.status === 'suspended' },
            { label: t('inst.archive'), danger: true, onClick: () => setStat(r, 'archived'), disabled: r.status === 'archived' },
          ]}
        />
      ) },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('inst.title')}</h1>
        <p className="admin-page__sub">{t('inst.subtitle')}</p>
      </header>
      <div className="admin-toolbar">
        <Tabs items={STATUSES.map((s) => ({ key: s, label: s }))} value={status} onChange={setStatus} />
      </div>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
        loading={q.status === 'loading'} empty={t('emptyGeneric')} />
    </div>
  );
}
